import React from "react";
import Loader from "@components/Loader";
import api from "@services/api";
import "@styles/opencart-admin.css";

/**
 * Editor for the order-status SMS message files (messagesList.{franchiseId}.json).
 *
 * Loads the per-target message set from GET /sms-messages/{target}, lets the
 * admin edit the title / message / deep link for every order type × language ×
 * status cell, and PUTs the whole structure back. The list of usable
 * {{$variables}} comes from the backend schema so it always matches the SMS
 * substitution engine (see WN-0092).
 */
export default function SmsMessagesPage() {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");

  const [schema, setSchema] = React.useState(null);
  const [targets, setTargets] = React.useState([]);
  const [target, setTarget] = React.useState("");
  const [isCustom, setIsCustom] = React.useState(true);

  const [messages, setMessages] = React.useState({});
  const [activeOrderType, setActiveOrderType] = React.useState("normal");
  const [activeLang, setActiveLang] = React.useState("1");

  // Track the last focused text field so a clicked variable is inserted there.
  const activeFieldRef = React.useRef(null);

  // ---- initial load: schema + list of targets ----------------------------
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sms-messages");
        if (!alive) return;
        setSchema(data.schema);
        setTargets(data.targets || []);
        const flashwash = (data.targets || []).find(
          (t) => String(t.id) === "4"
        );
        const first = (data.targets || [])[0];
        setTarget(String((flashwash || first)?.id ?? "default"));
      } catch (e) {
        if (alive) setError(readError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---- load messages whenever the selected target changes ----------------
  React.useEffect(() => {
    if (!target) return;
    let alive = true;
    setLoading(true);
    setNotice("");
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/sms-messages/${target}`);
        if (!alive) return;
        setMessages(data.messages || {});
        setIsCustom(!!data.is_custom);
      } catch (e) {
        if (alive) setError(readError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [target]);

  const orderTypes = schema?.order_types || ["normal", "complementary"];
  const languages = schema?.languages || { 1: "English", 2: "Arabic" };
  const statuses = schema?.statuses || {};
  const variables = schema?.variables || {};

  function getEntry(orderType, lang, status) {
    return messages?.[orderType]?.[lang]?.[status] || {};
  }

  function setField(orderType, lang, status, field, value) {
    setMessages((prev) => {
      const next = { ...prev };
      next[orderType] = { ...(next[orderType] || {}) };
      next[orderType][lang] = { ...(next[orderType][lang] || {}) };
      const entry = { ...(next[orderType][lang][status] || {}) };
      entry[field] = value;
      // Default a deep link so new cells stay consistent with existing ones.
      if (entry.deep_link == null) entry.deep_link = "order/status/?id=";
      next[orderType][lang][status] = entry;
      return next;
    });
  }

  function insertVariable(token) {
    const active = activeFieldRef.current;
    if (!active) {
      setNotice("Click inside a message or title field first, then a variable.");
      return;
    }
    const { orderType, lang, status, field, el } = active;
    const entry = getEntry(orderType, lang, status);
    const current = entry[field] || "";
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const nextVal = current.slice(0, start) + token + current.slice(end);
    setField(orderType, lang, status, field, nextVal);
    // Restore focus + caret just after the inserted token.
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        const pos = start + token.length;
        try {
          el.setSelectionRange(pos, pos);
        } catch (_) {}
      }
    });
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { data } = await api.put(`/sms-messages/${target}`, { messages });
      setMessages(data.messages || messages);
      setIsCustom(true);
      setTargets((prev) =>
        prev.map((t) =>
          String(t.id) === String(target) ? { ...t, has_file: true } : t
        )
      );
      setNotice("Saved. Changes are live for the next status-change SMS.");
    } catch (e) {
      setError(readError(e));
    } finally {
      setSaving(false);
    }
  }

  const currentTarget = targets.find((t) => String(t.id) === String(target));

  return (
    <div className="oc-content">
      {(loading || saving) && <Loader />}

      <div className="oc-header">
        <h1>SMS Messages</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>System</span>
          <span className="sep">›</span>
          <span>SMS Messages</span>
        </nav>
      </div>

      <div className="oc-card" style={{ marginBottom: 16 }}>
        <div className="form-row">
          <div className="form-group">
            <label>Marketplace / franchise</label>
            <select
              className="oc-input"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            >
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.has_file ? "  ✓ custom" : "  (inherits default)"}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Order type</label>
            <select
              className="oc-input"
              value={activeOrderType}
              onChange={(e) => setActiveOrderType(e.target.value)}
            >
              {orderTypes.map((ot) => (
                <option key={ot} value={ot}>
                  {ot}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Language</label>
            <select
              className="oc-input"
              value={activeLang}
              onChange={(e) => setActiveLang(e.target.value)}
            >
              {Object.entries(languages).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!isCustom && currentTarget && !currentTarget.is_default && (
          <div
            style={{
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              color: "#9a3412",
              padding: "8px 12px",
              borderRadius: 6,
              marginTop: 8,
            }}
          >
            <strong>{currentTarget.name}</strong> has no custom messages yet —
            you are editing a copy of the shared default. Saving will create a
            dedicated message file for this franchise only.
          </div>
        )}

        {error && (
          <div style={{ color: "#c00", marginTop: 8 }}>Error: {error}</div>
        )}
        {notice && (
          <div style={{ color: "#166534", marginTop: 8 }}>{notice}</div>
        )}
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div className="oc-card" style={{ flex: 1 }}>
          <div className="oc-toolbar" style={{ marginBottom: 12 }}>
            <strong>
              {activeOrderType} · {languages[activeLang]} messages
            </strong>
          </div>

          {Object.entries(statuses).map(([status, statusLabel]) => {
            const entry = getEntry(activeOrderType, activeLang, status);
            const isArabic = String(activeLang) === "2";
            return (
              <div
                key={status}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    color: "#374151",
                  }}
                >
                  {statusLabel}{" "}
                  <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                    ({status})
                  </span>
                </div>

                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label>Title</label>
                  <input
                    className="oc-input"
                    dir={isArabic ? "rtl" : "ltr"}
                    value={entry.title || ""}
                    onFocus={(e) =>
                      (activeFieldRef.current = {
                        orderType: activeOrderType,
                        lang: activeLang,
                        status,
                        field: "title",
                        el: e.target,
                      })
                    }
                    onChange={(e) =>
                      setField(
                        activeOrderType,
                        activeLang,
                        status,
                        "title",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label>Message</label>
                  <textarea
                    className="oc-input"
                    rows={4}
                    dir={isArabic ? "rtl" : "ltr"}
                    value={entry.message || ""}
                    onFocus={(e) =>
                      (activeFieldRef.current = {
                        orderType: activeOrderType,
                        lang: activeLang,
                        status,
                        field: "message",
                        el: e.target,
                      })
                    }
                    onChange={(e) =>
                      setField(
                        activeOrderType,
                        activeLang,
                        status,
                        "message",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Deep link</label>
                  <input
                    className="oc-input"
                    value={entry.deep_link || ""}
                    onFocus={(e) =>
                      (activeFieldRef.current = {
                        orderType: activeOrderType,
                        lang: activeLang,
                        status,
                        field: "deep_link",
                        el: e.target,
                      })
                    }
                    onChange={(e) =>
                      setField(
                        activeOrderType,
                        activeLang,
                        status,
                        "deep_link",
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
            );
          })}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 12,
            }}
          >
            <button
              className="oc-btn primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              Save messages
            </button>
          </div>
        </div>

        <div className="oc-card" style={{ width: 300, flexShrink: 0 }}>
          <div className="oc-toolbar" style={{ marginBottom: 12 }}>
            <strong>Variables</strong>
          </div>
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 0 }}>
            Click a field above, then a variable to insert it at the cursor.
            These are replaced with real order data when the SMS is sent.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {Object.entries(variables).map(([token, desc]) => (
              <button
                key={token}
                type="button"
                className="oc-btn ghost"
                style={{ textAlign: "left", whiteSpace: "normal" }}
                onClick={() => insertVariable(token)}
                title={desc}
              >
                <code style={{ fontSize: 12 }}>{token}</code>
                <div style={{ fontSize: 11, color: "#6b7280" }}>{desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function readError(e) {
  return (
    e?.response?.data?.message ||
    e?.message ||
    "Request failed. Please try again."
  );
}
